import React, { useEffect, useMemo, useState } from "react";
import { api, fmtUSD } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import AIAgentLoader from "./AIAgentLoader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  ArrowRight,
  Sparkles,
  Repeat,
  TrendingUp,
  TrendingDown,
  Clock,
  Gauge,
  ShieldAlert,
  Trophy,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const PROVIDER_COLORS = {
  anthropic: "#d97706",
  openai:    "#10a37f",
  google:    "#1a73e8",
  deepseek:  "#7c3aed",
};

function ProviderPill({ provider, label }) {
  const c = PROVIDER_COLORS[provider] || "#52525b";
  return (
    <span
      className="cs-pill"
      style={{ background: `${c}1a`, color: c }}
      data-testid={`provider-pill-${provider}`}
    >
      {label}
    </span>
  );
}

export default function TMAdvisorPanel() {
  // ── Shared catalog ────────────────────────────────────────────────────
  const [catalog, setCatalog] = useState(null);
  useEffect(() => {
    api.tm
      .catalog()
      .then(setCatalog)
      .catch(() => toast.error("Failed to load model catalog"));
  }, []);

  // ── Migration Simulation state ───────────────────────────────────────
  const [fromProvider, setFromProvider] = useState("openai");
  const [fromModel, setFromModel] = useState("gpt-5.2");
  const [toProvider, setToProvider] = useState("anthropic");
  const [toModel, setToModel] = useState("claude-sonnet-4-5");
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  // ── Advisor state ────────────────────────────────────────────────────
  const [objective, setObjective] = useState("balanced");
  const [task, setTask] = useState("general");
  const [budget, setBudget] = useState(500);
  const [adviceResult, setAdviceResult] = useState(null);
  const [adviceLoading, setAdviceLoading] = useState(false);

  // Model lists derived from catalog
  const providersInCatalog = useMemo(() => {
    if (!catalog) return [];
    const map = {};
    catalog.models.forEach((m) => {
      map[m.provider] = m.provider_label || m.provider;
    });
    return Object.entries(map).map(([id, label]) => ({ id, label }));
  }, [catalog]);

  const modelsFor = (providerId) =>
    catalog?.models.filter((m) => m.provider === providerId) || [];

  // Keep fromModel/toModel in sync when their provider changes
  useEffect(() => {
    if (!catalog) return;
    const list = modelsFor(fromProvider);
    if (list.length && !list.some((m) => m.id === fromModel)) {
      setFromModel(list[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromProvider, catalog]);
  useEffect(() => {
    if (!catalog) return;
    const list = modelsFor(toProvider);
    if (list.length && !list.some((m) => m.id === toModel)) {
      setToModel(list[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toProvider, catalog]);

  const runSimulation = async () => {
    setSimLoading(true);
    // Artificial small delay so the loader is visible — backend resolves <50ms
    const minDelay = new Promise((r) => setTimeout(r, 1100));
    try {
      const [res] = await Promise.all([
        api.tm.simulate({ from_model: fromModel, to_model: toModel }),
        minDelay,
      ]);
      setSimResult(res);
    } catch (e) {
      toast.error("Simulation failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSimLoading(false);
    }
  };

  const runAdvisor = async () => {
    setAdviceLoading(true);
    const minDelay = new Promise((r) => setTimeout(r, 1400));
    try {
      const [res] = await Promise.all([
        api.tm.advisor({
          objective,
          task,
          monthly_budget_usd: Number(budget) || 0,
        }),
        minDelay,
      ]);
      setAdviceResult(res);
    } catch (e) {
      toast.error("Advisor failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setAdviceLoading(false);
    }
  };

  if (!catalog) {
    return (
      <div className="cs-card p-8 text-center text-muted-foreground" data-testid="tm-advisor-loading">
        Loading model catalog…
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <AIAgentLoader
        open={simLoading}
        title="AI Agent is simulating the migration"
        subtitle="Modelling cost, quality and latency impact across your workload."
      />
      <AIAgentLoader
        open={adviceLoading}
        title="AI Agent is picking the best model for you"
        subtitle="Scoring every model on cost, quality and latency for your objective."
      />
      {/* ── Migration Simulation ─────────────────────────────────────── */}
      <div className="cs-card p-6 space-y-4" data-testid="tm-migration-card">
        <div className="flex items-start gap-3">
          <Repeat className="w-5 h-5 text-[#00579e] mt-0.5 shrink-0" />
          <div>
            <h3 className="cs-heading text-base font-semibold tracking-tight">
              Migration Simulation
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Estimate cost, quality and latency impact of switching models
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* FROM */}
          <div className="rounded-xl border border-border p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
              From
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                Provider
              </Label>
              <Select value={fromProvider} onValueChange={setFromProvider}>
                <SelectTrigger className="rounded-lg h-9 text-xs mt-1" data-testid="sim-from-provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {providersInCatalog.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="text-xs">
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                Model
              </Label>
              <Select value={fromModel} onValueChange={setFromModel}>
                <SelectTrigger className="rounded-lg h-9 text-xs mt-1 cs-mono" data-testid="sim-from-model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {modelsFor(fromProvider).map((m) => (
                    <SelectItem key={m.id} value={m.id} className="text-xs cs-mono">
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* TO */}
          <div className="rounded-xl border border-border p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
              To
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                Provider
              </Label>
              <Select value={toProvider} onValueChange={setToProvider}>
                <SelectTrigger className="rounded-lg h-9 text-xs mt-1" data-testid="sim-to-provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {providersInCatalog.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="text-xs">
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                Model
              </Label>
              <Select value={toModel} onValueChange={setToModel}>
                <SelectTrigger className="rounded-lg h-9 text-xs mt-1 cs-mono" data-testid="sim-to-model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {modelsFor(toProvider).map((m) => (
                    <SelectItem key={m.id} value={m.id} className="text-xs cs-mono">
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <Button
          onClick={runSimulation}
          disabled={simLoading}
          className="w-full bg-[#0f172a] hover:bg-[#1e293b] text-white rounded-full h-10 text-xs"
          data-testid="sim-run"
        >
          {simLoading ? <Loader2 className="w-4 h-4 cs-spin" /> : "Run Simulation"}
        </Button>

        {simResult && simResult.success && <SimResultCard result={simResult} />}
      </div>

      {/* ── AI Model Advisor ─────────────────────────────────────────── */}
      <div className="cs-card p-6 space-y-4" data-testid="tm-advisor-card">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#6d28d9] mt-0.5 shrink-0" />
          <div>
            <h3 className="cs-heading text-base font-semibold tracking-tight">
              AI Model Advisor
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Recommend the best model for your workload
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
              Objective
            </Label>
            <Select value={objective} onValueChange={setObjective}>
              <SelectTrigger className="rounded-lg h-9 text-xs mt-1" data-testid="adv-objective">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {catalog.objectives.map((o) => (
                  <SelectItem key={o.id} value={o.id} className="text-xs">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
              Primary Task
            </Label>
            <Select value={task} onValueChange={setTask}>
              <SelectTrigger className="rounded-lg h-9 text-xs mt-1" data-testid="adv-task">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {catalog.tasks.map((t) => (
                  <SelectItem key={t.id} value={t.id} className="text-xs">
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
              Monthly Budget (USD)
            </Label>
            <Input
              type="number"
              min="0"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className="rounded-lg h-9 text-xs cs-mono mt-1"
              data-testid="adv-budget"
            />
          </div>
        </div>

        <Button
          onClick={runAdvisor}
          disabled={adviceLoading}
          className="w-full bg-[#6d28d9] hover:bg-[#5b21b6] text-white rounded-full h-10 text-xs"
          data-testid="adv-run"
        >
          {adviceLoading ? <Loader2 className="w-4 h-4 cs-spin" /> : "Run Advisor"}
        </Button>

        {adviceResult?.recommendations && <AdvisorResults result={adviceResult} />}
      {adviceResult?.recommendations?.every((r) => !r.within_budget) && (
        <div
          className="rounded-lg border border-[#f59e0b]/40 bg-[#fff7ed] p-2.5 text-[11px] text-[#9a3412]"
          data-testid="adv-budget-warning"
        >
          ⚠ No model fits your ${budget}/mo budget at the default workload
          (250K calls × 1.8K input + 600 output tokens). Increase the budget or
          reduce expected call volume.
        </div>
      )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function SimResultCard({ result }) {
  const { from, to, delta, recommendation } = result;
  const saveTone = delta.monthly_usd < 0;
  const TrendIcon = saveTone ? TrendingDown : TrendingUp;
  const riskColor =
    delta.risk === "high" ? "#b91c1c" : delta.risk === "medium" ? "#a16207" : "#15803d";

  return (
    <div className="space-y-3 mt-3" data-testid="sim-result">
      <div className="rounded-xl bg-[hsl(var(--cs-tint))] border border-border p-4 flex items-center gap-3">
        <div className="flex-1">
          <div className="cs-mono text-xs text-muted-foreground">{from.label}</div>
          <div className="cs-mono text-sm font-semibold text-[#00579e]">
            {fmtUSD(from.monthly_cost_usd, { compact: true })}/mo
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground" />
        <div className="flex-1 text-right">
          <div className="cs-mono text-xs text-muted-foreground">{to.label}</div>
          <div className="cs-mono text-sm font-semibold text-[#00579e]">
            {fmtUSD(to.monthly_cost_usd, { compact: true })}/mo
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className={`rounded-lg border p-3 ${saveTone ? "border-[#15803d]/30 bg-[#dcfce7]" : "border-[#be185d]/30 bg-[#fce7f3]"}`}>
          <div className="flex items-center gap-1.5 mb-1">
            <TrendIcon className="w-3.5 h-3.5" style={{ color: saveTone ? "#15803d" : "#be185d" }} />
            <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">
              Monthly
            </span>
          </div>
          <div className={`cs-mono text-sm font-semibold ${saveTone ? "text-[#15803d]" : "text-[#be185d]"}`}>
            {delta.monthly_usd >= 0 ? "+" : ""}
            {fmtUSD(delta.monthly_usd, { compact: true })}
          </div>
          <div className="text-[10px] cs-mono text-muted-foreground mt-0.5">
            {delta.pct >= 0 ? "+" : ""}
            {delta.pct}%
          </div>
        </div>

        <div className="rounded-lg border border-border p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Gauge className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">
              Quality
            </span>
          </div>
          <div className="cs-mono text-sm font-semibold">
            {delta.quality_points >= 0 ? "+" : ""}
            {delta.quality_points} pts
          </div>
          <div className="text-[10px] cs-mono text-muted-foreground mt-0.5">
            {from.quality} → {to.quality}
          </div>
        </div>

        <div className="rounded-lg border border-border p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">
              Latency
            </span>
          </div>
          <div className="cs-mono text-sm font-semibold">
            {delta.latency_ms >= 0 ? "+" : ""}
            {delta.latency_ms}ms
          </div>
          <div className="text-[10px] cs-mono text-muted-foreground mt-0.5">
            {from.latency_ms} → {to.latency_ms}ms
          </div>
        </div>
      </div>

      <div
        className="rounded-lg border border-border p-3 flex items-start gap-2"
        data-testid="sim-verdict"
        style={{ borderLeftWidth: 4, borderLeftColor: riskColor }}
      >
        <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" style={{ color: riskColor }} />
        <div className="text-xs leading-relaxed">
          <div className="font-semibold text-foreground">{recommendation}</div>
          <div className="text-muted-foreground mt-1">
            Annual impact:{" "}
            <span className="cs-mono font-semibold">
              {delta.annual_usd >= 0 ? "+" : ""}
              {fmtUSD(delta.annual_usd, { compact: true })}
            </span>{" "}
            · risk band:{" "}
            <span className="cs-mono uppercase text-[10px] tracking-[0.1em]" style={{ color: riskColor }}>
              {delta.risk}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function AdvisorResults({ result }) {
  const { recommendations, objective, task, monthly_budget_usd } = result;
  return (
    <div className="space-y-2 mt-3" data-testid="adv-result">
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
        Top 3 picks · objective: <span className="font-semibold">{objective.label}</span> · task:{" "}
        <span className="font-semibold">{task.label}</span> · budget {fmtUSD(monthly_budget_usd, { compact: true })}
      </div>
      {recommendations.map((r) => {
        const top = r.rank === 1;
        return (
          <div
            key={r.model_id}
            className={`rounded-xl border p-3 ${
              top ? "border-[#6d28d9]/50 bg-[#f5f3ff]" : "border-border bg-white"
            }`}
            data-testid={`adv-rec-${r.rank}`}
          >
            <div className="flex items-center gap-2 mb-1.5">
              {top && <Trophy className="w-3.5 h-3.5 text-[#6d28d9]" />}
              <span className="cs-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
                #{r.rank}
              </span>
              <ProviderPill provider={r.provider} label={r.provider_label} />
              <span className="cs-mono text-sm font-semibold text-foreground">{r.label}</span>
              <span className="ml-auto cs-pill bg-[#dbeafe] text-[#00579e]">
                score {r.score}
              </span>
              {r.within_budget ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-[#15803d]" />
              ) : null}
            </div>

            <div className="grid grid-cols-4 gap-2 text-xs cs-mono">
              <div>
                <span className="text-muted-foreground">$/mo</span>{" "}
                <span className="font-semibold text-[#00579e]">
                  {fmtUSD(r.monthly_cost_usd, { compact: true })}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Quality</span>{" "}
                <span className="font-semibold">{r.quality}/100</span>
              </div>
              <div>
                <span className="text-muted-foreground">Latency</span>{" "}
                <span className="font-semibold">{r.latency_ms}ms</span>
              </div>
              <div>
                <span className="text-muted-foreground">Ctx</span>{" "}
                <span className="font-semibold">{r.context_k}K</span>
              </div>
            </div>

            <div className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
              {r.rationale}
            </div>
          </div>
        );
      })}
    </div>
  );
}
